const SHOPIFY_COLLECTION_SCRIPT_ATTRIBUTE =
  'data-shopify-collection-script';
const SHOPIFY_COLLECTION_SCRIPT_URL_ATTRIBUTE =
  'data-shopify-collection-script-url';
const SHOPIFY_COLLECTION_READY_EVENT = 'gld:collection:shopify-ready';
const SHOPIFY_COLLECTION_READY_KEY = '__gldShopifyCollectionModulesReady';
const SHOPIFY_COLLECTION_PROMISE_KEY = '__gldShopifyCollectionModulesPromise';

const getWindow = (element) =>
  element?.ownerDocument?.defaultView ||
  (typeof window !== 'undefined' ? window : null);

const getCollectionRoot = (element) =>
  element?.closest?.('[data-collection-section]') || element;

/**
 * Loads the Shopify collection controller only when Shopify actually owns the
 * page. Forced Constructor collections use it after a fallback; an A/B
 * collection uses it only for the control arm.
 */
export const loadShopifyCollectionModules = (element) => {
  const root = getCollectionRoot(element);
  const currentWindow = getWindow(root);
  const document = root?.ownerDocument || currentWindow?.document;

  if (!currentWindow || !document) {
    return Promise.resolve(false);
  }

  if (currentWindow[SHOPIFY_COLLECTION_READY_KEY]) {
    return Promise.resolve(true);
  }

  if (currentWindow[SHOPIFY_COLLECTION_PROMISE_KEY]) {
    return currentWindow[SHOPIFY_COLLECTION_PROMISE_KEY];
  }

  const source = root?.getAttribute?.(
    SHOPIFY_COLLECTION_SCRIPT_URL_ATTRIBUTE
  );

  if (!source) {
    return Promise.resolve(false);
  }

  const existing = document.querySelector?.(
    `[${SHOPIFY_COLLECTION_SCRIPT_ATTRIBUTE}]`
  );
  const script = existing || document.createElement('script');

  currentWindow[SHOPIFY_COLLECTION_PROMISE_KEY] = new Promise(
    (resolve, reject) => {
      const handleReady = () => {
        currentWindow[SHOPIFY_COLLECTION_READY_KEY] = true;
        resolve(true);
      };
      const handleError = () => {
        currentWindow[SHOPIFY_COLLECTION_PROMISE_KEY] = null;
        reject(new Error('The Shopify collection controller failed to load.'));
      };

      if (existing) {
        currentWindow.addEventListener(
          SHOPIFY_COLLECTION_READY_EVENT,
          handleReady,
          { once: true }
        );
        script.addEventListener?.('error', handleError, { once: true });
        return;
      }

      script.src = source;
      script.async = true;
      script.setAttribute(SHOPIFY_COLLECTION_SCRIPT_ATTRIBUTE, '');
      script.addEventListener('load', handleReady, { once: true });
      script.addEventListener('error', handleError, { once: true });
      (document.head || document.documentElement).appendChild(script);
    }
  );

  return currentWindow[SHOPIFY_COLLECTION_PROMISE_KEY];
};

export const markShopifyCollectionModulesReady = (currentWindow = window) => {
  currentWindow[SHOPIFY_COLLECTION_READY_KEY] = true;
  currentWindow.dispatchEvent(
    new currentWindow.CustomEvent(SHOPIFY_COLLECTION_READY_EVENT)
  );
};

export default loadShopifyCollectionModules;
